import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';

import type { ActiveThreadStore } from './active-thread-store';
import { createActiveThreadStore } from './active-thread-store';
import { useDeactivateOnOutsideClick } from './anchor/outside-click';
import { CommentsScopeContext } from './comments-scope-context';

export const ActiveThreadStoreContext = createContext<StoreApi<ActiveThreadStore> | null>(null);

type ActiveThreadStoreProviderProps = {
    readonly initialStore?: StoreApi<ActiveThreadStore>;
};

const OutsideClickDeactivation: FunctionComponent = () => {
    useDeactivateOnOutsideClick();
    return null;
};

const ScopeChangeReset: FunctionComponent = () => {
    const scope = useContext(CommentsScopeContext);
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const setCommentClassification = useActiveThreadStore((state) => state.setCommentClassification);
    const setViewMode = useActiveThreadStore((state) => state.setViewMode);
    const cancelPending = useActiveThreadStore((state) => state.cancelPending);
    const scopeKey = is.null(scope)
        ? null
        : `${scope.architectureId}|${scope.itemId}|${scope.requestedVersion}|${scope.language}`;
    const previousScopeKeyRef = useRef<string | null>(scopeKey);

    useEffect(() => {
        if (is.null(scopeKey)) return;
        if (previousScopeKeyRef.current === scopeKey) return;
        previousScopeKeyRef.current = scopeKey;
        cancelPending();
        setActiveThreadId(null);
        setCommentClassification(null);
        setViewMode('active');
    }, [scopeKey, cancelPending, setActiveThreadId, setCommentClassification, setViewMode]);

    return null;
};

export const ActiveThreadStoreProvider: FunctionComponent<PropsWithChildren<ActiveThreadStoreProviderProps>> = ({
    children,
    initialStore,
}) => {
    const storeRef = useRef<StoreApi<ActiveThreadStore> | null>(null);
    storeRef.current ??= initialStore ?? createActiveThreadStore();

    return (
        <ActiveThreadStoreContext.Provider value={storeRef.current}>
            <OutsideClickDeactivation />
            <ScopeChangeReset />
            {children}
        </ActiveThreadStoreContext.Provider>
    );
};

export const useActiveThreadStore = <T,>(selector: (state: ActiveThreadStore) => T): T => {
    const store = useContext(ActiveThreadStoreContext);

    if (is.null(store)) {
        throw new Error('useActiveThreadStore must be used inside <ActiveThreadStoreProvider>');
    }

    return useStore(store, selector);
};

export const useActiveThreadStoreApi = (): StoreApi<ActiveThreadStore> => {
    const store = useContext(ActiveThreadStoreContext);

    if (is.null(store)) {
        throw new Error('useActiveThreadStoreApi must be used inside <ActiveThreadStoreProvider>');
    }

    return store;
};
